import axios from 'axios';

/**
 * Determine the base URL based on environment
 * In development, use the proxy setting
 * In production, use the actual API URL
 */
const getBaseUrl = () => {
  // In production, we need to use the actual backend API URL
  // In development, we can use the proxy configured in package.json
  if (process.env.NODE_ENV === 'production') {
    // Use environment variable if available, otherwise fallback to a default
    const baseUrl = process.env.REACT_APP_API_URL || 'https://meira-server.onrender.com';
    return baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;
  }
  return '/api'; // For development
};

/**
 * Axios instance for making API requests
 * Configured with base URL and request/response interceptors
 */
const api = axios.create({
  baseURL: getBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const token = localStorage.getItem('token');
    
    // If token exists, add it to request headers
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Flag to prevent multiple unauthorized alerts
let isRefreshing = false;
let unauthorizedRedirectTimeout = null;

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle common errors
    if (error.response) {
      // Server responded with a status code outside of 2xx range
      console.error('Response error:', error.response.status, error.response.data);
      
      // Adding more detailed error information for debugging
      error.statusCode = error.response.status;
      error.details = error.response.data;
      
      // Handle 401 Unauthorized errors (expired or invalid token)
      if (error.response.status === 401) {
        // Remove token to prevent further failed requests
        localStorage.removeItem('token');
        delete api.defaults.headers.common['Authorization'];
        
        // Prevent multiple alerts for auth errors
        if (!isRefreshing) {
          isRefreshing = true;
          
          // Clear any existing timeout
          if (unauthorizedRedirectTimeout) {
            clearTimeout(unauthorizedRedirectTimeout);
          }
          
          // Set timeout for redirect to prevent immediate redirects on multiple API calls
          unauthorizedRedirectTimeout = setTimeout(() => {
            // Redirect to login (if in browser environment)
            if (typeof window !== 'undefined') {
              window.location.href = '/login?session=expired';
            }
            isRefreshing = false;
          }, 500);
        }
      }
      
      // Handle 403 Forbidden errors (insufficient permissions)
      if (error.response.status === 403) {
        console.error('Permission denied:', error.response.data.message);
      }
      
      // Handle 405 Method Not Allowed errors
      if (error.response.status === 405) {
        console.error('Method not allowed. Check API endpoint and HTTP method:', 
          error.config.method, 
          error.config.url
        );
      }
      
      // Handle 429 Too Many Requests (rate limiting)
      if (error.response.status === 429) {
        console.error('Rate limit exceeded:', error.response.data.message);
      }
    } else if (error.request) {
      // Request was made but no response was received
      console.error('No response received:', error.request);
      error.statusCode = 0;
      error.details = { message: 'Network error - no response received' };
    } else {
      // Error setting up request
      console.error('Request error:', error.message);
      error.statusCode = 0;
      error.details = { message: 'Request setup error: ' + error.message };
    }
    
    return Promise.reject(error);
  }
);

export default api;
